import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { CmmContainer, CmmSubTitle, SalesTable, SalesTableBody, SalesTableCell, SalesTableRow } from 'components/Dashboard';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { CombinePairsChart } from 'components/Chart';
import { DataGrid } from '@mui/x-data-grid';
import CompanySales from './CompanySales';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import PgmVersionComponent from '../../../components/Pgm/PgmVersionComponent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../../_state';

const columns = [
  { field: 'id', headerName: '월', hide: true, width: 120, headerAlign: 'center', align: 'center', valueGetter: (params) => `${Number(params?.value.split('-')[1])}월` },
  { field: 'orderCnt', headerName: '주문건수', headerAlign: 'center', align: 'right', valueGetter: (params) => params?.value?.toLocaleString() },
  { field: 'couponCnt', headerName: '사용권개수', headerAlign: 'center', align: 'right', valueGetter: (params) => params?.value?.toLocaleString() },
  { field: 'totalSales', headerName: '월별매출', headerAlign: 'center', align: 'right', flex: 1, valueGetter: (params) => params?.value?.toLocaleString() },
  { field: 'cmltSales', headerName: '매출누계', headerAlign: 'center', align: 'right', flex: 1, valueGetter: (params) => params?.value?.toLocaleString() }
];

const pgmColumns = [
  { field: 'engNm', headerName: '제품명(영문)', width: 225, headerAlign: 'center', align: 'left' },
  { field: 'korNm', headerName: '제품명(한글)', width: 225, headerAlign: 'center', align: 'left' },
  {
    field: 'totalCnt',
    headerName: '누적 실행 수',
    width: 100,
    headerAlign: 'center',
    align: 'right',
    valueGetter: (params) => Number(params?.value)?.toLocaleString('en-US')
  },
  { field: 'rmk', headerName: '비고', width: 200, headerAlign: 'center', align: 'left' }
];

export default function AnalysisSales() {
  const [monthChartParams, setMonthChartParams] = useState({});
  const [monthRows, setMonthRows] = useState([]);
  const [pgmRows, setPgmRows] = useState([]);
  const [datas, setDatas] = useState({});
  const [srchData, setSrchData] = useState({
    cond: 'month'
  });
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(moment().format('YYYY'));
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(moment().format('MM'));

  const [versionId, setVersionId] = useState('');
  const [versionOpen, setVersionOpen] = useState(false);
  const [pgmNm, setPgmNm] = useState('');

  const auth = useRecoilValue(authAtom);

  const handleRowClick = (params) => {
    setDatas(params.row);
  };

  const handleVersionOpen = () => setVersionOpen(true);
  const handleVersionClose = () => setVersionOpen(false);

  const getYears = async () => {
    const response = await axios.get('/api/dashboard/anls-sales/years');
    if (response) {
      setYears(response.data);
      setSrchData({ ...srchData, year: response.data[0] });
    }
  };

  const getMonths = async () => {
    let tempMonths = [];
    for (let i = 1; i <= 12; i++) {
      tempMonths = [...tempMonths, i];
    }
    setMonths(tempMonths);
  };

  const getMonthChart = (params) => {
    const categories = params.map((t) => `${Number(t?.id.split('-')[1])}월`);
    const series = [
      {
        name: '매출',
        type: 'line',
        data: params.map((t, idx) => (idx < month ? t?.cmltSales : null)),
        yaxis: 0
      },
      {
        name: '주문 누계',
        type: 'line',
        data: params.map((t, idx) => (idx < month ? t?.cmltOrder : null)),
        yaxis: 1
      },
      {
        name: '사용권 누계',
        type: 'line',
        data: params.map((t, idx) => (idx < month ? t?.cmltCoupon : null)),
        yaxis: 1
      },
      {
        name: '사용 사용권',
        type: 'line',
        data: params.map((t, idx) => (idx < month ? t?.useActiveCode : null)),
        yaxis: 1
      }
    ];
    const yaxis = [
      {
        showAlways: true,
        title: {
          text: '매출'
        }
      },
      {
        showAlways: true,
        opposite: true,
        title: {
          text: '주문 / 사용권'
        }
      },
      {
        show: false,
        opposite: true,
        title: {
          text: series[2].name
        }
      },
      {
        show: false,
        opposite: true,
        title: {
          text: series[2].name
        }
      }
    ];
    return { categories, series, yaxis };
  };

  const getChartMonth = async () => {
    const { cond } = srchData;
    const { startYmd, endYmd } = datas;
    await axios
      .get('/api/dashboard/anls-sales/chart', { params: { cond, year, startYmd, endYmd } })
      .then((response) => {
        const params = response.data;
        const result = getMonthChart(params);
        setMonthChartParams({ ...result });
        setMonthRows(params);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getPgmList = async (scale) => {
    return await axios
      .get(`/api/pgm`)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        alert(error.data.message);
      });
  };

  useEffect(() => {
    getYears();
    getMonths();
  }, [auth]);

  useEffect(async () => {
    if (auth) {
      getChartMonth();
      await getPgmList().then((result) => {
        setPgmRows(
          result.sort((a, b) => {
            return Number(a.totalCnt) > Number(b.totalCnt) ? -1 : Number(a.totalCnt) < Number(b.totalCnt) ? 1 : 0;
          })
        );
      });
    }
  }, [year, month, auth]);

  useEffect(() => {
    if (datas?.startYmd && datas?.endYmd) getChartMonth();
  }, [datas]);

  return (
    <Box sx={{ display: 'block', minWidth: 800 }}>
      <Stack spacing={2} sx={{ maxWidth: 800 }}>
        <CmmContainer sx={{ width: '100%', display: 'block' }}>
          <Stack direction="row" spacing={2}>
            <Select
              labelId="gbn-label"
              name="year"
              value={year || ''}
              label="검색기준"
              onChange={(e) => {
                setYear(e.target.value);
              }}
            >
              {years.map((year, idx) => (
                <MenuItem key={idx} value={year}>
                  {year}년
                </MenuItem>
              ))}
            </Select>

            <Select
              labelId="gbn-label"
              name="month"
              value={Number(month) || ''}
              label="검색기준"
              onChange={(e) => {
                setMonth(e.target.value);
              }}
            >
              {months.map((month, idx) => (
                <MenuItem key={idx} value={month}>
                  {month}월
                </MenuItem>
              ))}
            </Select>
            <Typography component="h1" variant="h3" sx={{ textAlign: 'center', width: '100%' }}>
              도화 앱스
            </Typography>
          </Stack>
        </CmmContainer>
        <Box sx={{ flexGrow: 1 }}>
          <Stack>
            <Grid item xs={7} sm={7} md={7}>
              <CmmContainer>{monthChartParams?.series && <CustomCombinePairsChart title={`${year}년`} categories={monthChartParams?.categories} series={monthChartParams?.series} yaxis={monthChartParams?.yaxis} />}</CmmContainer>
            </Grid>
            <Grid item xs={5} sm={5} md={5} sx={{ mt: 2 }}>
              <CmmContainer>
                <CmmSubTitle title="월별 실적 상세" />
                <DataGrid rowHeight={40} rows={monthRows.filter((row) => Number(row.id.split('-')[1]) <= Number(month))} columns={columns} hideFooter onRowClick={handleRowClick} />
                <SalesTable>
                  <SalesTableBody>
                    <SalesTableRow>
                      <SalesTableCell th sx={{ textAlign: 'center', width: 320 }}>
                        총 매출
                      </SalesTableCell>
                      <SalesTableCell th unit="원" sx={{ textAlign: 'right', width: 440 }}>
                        {monthRows[monthRows.length - 1]?.cmltSales.toLocaleString() || 0}
                      </SalesTableCell>
                    </SalesTableRow>
                  </SalesTableBody>
                </SalesTable>
              </CmmContainer>
            </Grid>
            <Grid item xs={2} sm={5} md={5} sx={{ mt: 2 }}>
              <CmmContainer>
                <CmmSubTitle title={`제품 목록 (${pgmRows.length})`} />
                <DataGrid
                  rowHeight={40}
                  rows={pgmRows}
                  columns={pgmColumns}
                  hideFooter
                  onCellClick={async (params, event) => {
                    const row = params.row;
                    if (!!row?.versionId) {
                      await setPgmNm(row.engNm);
                      await setVersionId(row?.versionId);
                      await handleVersionOpen();
                    }
                  }}
                />
              </CmmContainer>
            </Grid>
          </Stack>
        </Box>
      </Stack>
      <CompanySales srchData={srchData} />
      <Dialog open={versionOpen} onClose={handleVersionClose} aria-labelledby="draggable-dialog-title" maxWidth="800">
        <DialogTitle id="draggable-dialog-title">{pgmNm} 버전 내역</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 3 }}>
            <PgmVersionComponent id={versionId} handleVersionClose={handleVersionClose} setVersionId={setVersionId} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="success" onClick={handleVersionClose}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const CustomCombinePairsChart = (props) => {
  const { categories, series, yaxis, title } = props;

  return (
    <CombinePairsChart
      title={title}
      categories={categories}
      series={series}
      yaxis={yaxis}
      tooltip={{
        y: [
          {
            formatter: function (val) {
              return val + ' (원)';
            }
          },
          {
            formatter: function (val) {
              return val + ' (건)';
            }
          },
          {
            formatter: function (val) {
              return val + ' (개)';
            }
          }
        ]
      }}
    />
  );
};
